import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import window from 'ember-window-mock';
import { QontoHeader } from 'receivable-invoices-js/react/components/shared/qonto-header';
import { YOUSIGN } from 'receivable-invoices-js/constants/quotes';
import { Loading } from 'receivable-invoices-js/react/components/shared/loading';
import { useRouteQuery } from './use-route-query';
import styles from './layout.module.css';

export function QuotesSigningRoute(): ReactNode {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const {
    signatureLink,
    isClientPortalEnabled,
    clientId,
    quoteId,
    isError,
    isNotAvailable,
    isLoading,
  } = useRouteQuery();

  useEffect(() => {
    if (isError) {
      void navigate(`/quotes/${quoteId}/signing/${clientId}/error`, { replace: true });
    }
    if (isNotAvailable) {
      void navigate(`/quotes/${quoteId}/signing/${clientId}/not-available`, { replace: true });
    }
  }, [navigate, quoteId, clientId, isError, isNotAvailable]);

  useEffect(() => {
    function handleYousignEvent(e: { origin: string; data: { type: string; event: string } }) {
      if (e.origin === YOUSIGN.EVENT_ORIGIN && e.data.type === YOUSIGN.EVENT_TYPE) {
        if (e.data.event === 'error') {
          void navigate(`/quotes/${quoteId}/signing/${clientId}/signature-failed`, {
            replace: true,
          });
        }
      }
    }

    window.addEventListener('message', handleYousignEvent);
    return () => {
      window.removeEventListener('message', handleYousignEvent);
    };
  }, [navigate, quoteId, clientId]);

  if (isLoading) {
    return <Loading spinnerSize="large" />;
  }

  return (
    <>
      <header className={styles.qontoHeader} data-test-qonto-header>
        <QontoHeader />
        {isClientPortalEnabled ? (
          <a
            className="btn"
            data-test-client-portal-cta
            href={`/clients/${clientId}`}
            type="button"
          >
            <FormattedMessage id="quote.preview.cta.portal" />
          </a>
        ) : null}
      </header>
      <main className={styles.iframeContainer}>
        <iframe
          data-test-signature
          referrerPolicy="strict-origin-when-cross-origin"
          src={`${signatureLink}&disable_domain_validation=true`}
          title={formatMessage({ id: 'quotes.e-signature.iframe.title' })}
        />
      </main>
    </>
  );
}
